import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zafClient, searchUsers } from '@services/Zendesk';
import { buildSearchQuery } from '@constants/buildSearchQuery';
import { useUserContext } from '@context/UserContext';
import type { User } from 'types/UserZendesk';
import { formateCpf } from './masks';

export function useUserManagement() {
  const { setSelectedUser, setNewUserFormData } = useUserContext();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [noUsers, setNoUsers] = useState('');

  const searchUsersHandler = async () => {
    if (!searchTerm) return;

    try {
      setLoading(true);

      const numericTerm = searchTerm.replace(/\D/g, '');

      let foundUsers: User[] = [];

      if (numericTerm.length === 11) {
        const formattedCpf = formateCpf(numericTerm);

        const [withMask, withoutMask] = await Promise.all([
          searchUsers(zafClient, `cpf_3:${formattedCpf}`),
          searchUsers(zafClient, `cpf_3:${numericTerm}`)
        ]);

        const uniqueUsers = [
          ...withMask,
          ...withoutMask
        ].filter(
          (user, index, self) =>
            index === self.findIndex(u => u.id === user.id)
        );

        foundUsers = uniqueUsers;

      } else {
        const query = buildSearchQuery(searchTerm);
        foundUsers = await searchUsers(zafClient, query);
      }

      setUsers(foundUsers);
      setNoUsers(foundUsers.length === 0 ? 'Nenhum usuário encontrado' : '');

    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectExistingUser = (user: User, selectedRole: string) => {
    setSelectedUser({
      ...user,
      role: selectedRole,
    });
    setNewUserFormData({ name: '', cpf_3: '', phone: '', email: '', cnpj_regex: '', tipoConta: '' });
    navigate('/review-user-account');
  };

  return {
    searchTerm,
    setSearchTerm,
    users,
    loading,
    searchUsersHandler,
    selectExistingUser,
    noUsers
  };
}